import api from './api.ts';

export type LearningKind = 'example' | 'rule';
export type LearningSource = 'history' | 'continuous' | 'chat_correction' | 'chat_instruction';
export type MessageLearningAction = 'correction' | 'instruction';
export type MessageAuthorType = 'patient' | 'platform_human' | 'connected_whatsapp_human' | 'agent' | 'automation';

export interface LearningContextMessage {
  id: number;
  type: 'text' | 'audio';
  text: string;
  available_text: boolean;
}

export interface LearningContextChoice extends LearningContextMessage {
  from_me: boolean;
  author_type?: MessageAuthorType | null;
  action?: MessageLearningAction | null;
  created_at: string;
}

export interface LearningContextSelection {
  input_message_ids: number[];
  output_message_ids: number[];
  // Omitted preserves the captured history; [] explicitly removes it.
  history_message_ids?: number[];
  before_id?: number;
}

export type LearningHistorySource = 'runner_snapshot' | 'reconstructed' | 'unavailable';

export interface LearningHistoryMessage {
  id: number | null;
  role: 'client' | 'team' | 'agent';
  origin: string;
  type: string;
  text: string;
  source_hash?: string;
  timestamp?: string | null;
  scope_proof?: 'verified' | 'unavailable';
}

export interface LearningConversationContext {
  version?: number;
  source?: LearningHistorySource;
  messages?: LearningHistoryMessage[];
  scope?: Record<string, unknown>;
  limits?: Record<string, unknown>;
}

export interface LearningContext {
  learning_context_version?: number;
  mode: 'turn' | 'manual';
  action: MessageLearningAction;
  state: 'processing' | 'completed' | 'partial' | 'failed' | 'suppressed' | 'manual';
  input_messages: LearningContextMessage[];
  output_messages: LearningContextMessage[];
  user_example: string;
  assistant_example: string;
  source_message_ids: number[];
  fingerprint: string;
  run_id: string | null;
  history_messages?: LearningHistoryMessage[];
  history_source?: LearningHistorySource;
  history_message_ids?: number[];
  conversation_context?: LearningConversationContext;
  history_selection?: {
    max_messages: number;
    available_messages: LearningHistoryMessage[];
    has_more: boolean;
    before_id: number | null;
  };
  can_publish: boolean;
  warnings: string[];
  limits: { max_input_characters: number; max_response_characters: number; context_exceeds_limit: boolean };
  manual_selection: {
    required: boolean;
    max_messages: number;
    available_messages: LearningContextChoice[];
    has_more: boolean;
    before_id: number | null;
  };
}

export interface LearningCorrectionContext extends LearningContextSelection {
  context_fingerprint: string;
  kind?: LearningKind;
  when_to_use?: string;
}

export interface LearningSourceMessage {
  id: number;
  content: string;
  from_me: boolean;
  author_type?: MessageAuthorType | null;
  author_name?: string | null;
  created_at: string;
  contact_id?: number | null;
}

export interface LearningCandidate {
  id: number;
  title: string;
  kind: LearningKind;
  when_to_use: string;
  instruction: string;
  user_example: string;
  assistant_example: string;
  source: LearningSource;
  status: string;
  created_at: string;
  source_messages?: LearningSourceMessage[];
  context_available?: boolean;
  conversation_contact_id?: number | null;
  conversation_contact_phone?: string | null;
  conversation_context?: LearningConversationContext;
  context_access?: 'available' | 'unavailable';
  prompt_eligible?: boolean;
  prompt_character_count?: number;
}

export interface LearningItem extends LearningCandidate {
  version: number;
  active: boolean;
  indexing?: boolean;
  origin?: LearningSource;
  updated_at: string;
}

export interface LearningPage<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface LearningSettings {
  enabled: boolean;
  runtime_enabled: boolean;
  library_version: number;
}

export interface LearningStatus extends LearningSettings {
  counts: {
    pending: number;
    published: number;
    indexing: number;
    jobs_queued: number;
    jobs_paused_budget?: number;
    jobs_failed?: number;
  };
  last_completed_at?: string | null;
  latest_failed_job?: LearningJob | null;
}

export interface LearningHistoryPreview {
  eligible_messages: number;
  selected_messages: number;
  max_messages: number;
  from_date: string;
  to_date: string;
  requires_activation: boolean;
}

export interface LearningJob {
  id: number;
  kind: string;
  status: string;
  params: { from_date: string; to_date: string; max_messages: number };
  counts: Record<string, number>;
  error_code?: string | null;
  can_retry?: boolean;
  created_at: string;
  updated_at: string;
}

export type LearningEdit = Partial<Pick<LearningCandidate, 'title' | 'kind' | 'when_to_use' | 'instruction' | 'user_example' | 'assistant_example'>>;

const base = (clinicId: number) => `/api/clinics/${clinicId}/agent-learning`;

export const learningApi = {
  async settings(clinicId: number) {
    return (await api.get<LearningSettings>(`${base(clinicId)}/settings`)).data;
  },
  async status(clinicId: number) {
    return (await api.get<LearningStatus>(`${base(clinicId)}/status`)).data;
  },
  async candidates(clinicId: number, page = 1, pageSize = 20, q = '', source = '', createdFrom = '') {
    return (await api.get<LearningPage<LearningCandidate>>(`${base(clinicId)}/candidates`, { params: { status: 'pending', page, page_size: pageSize, ...(q ? { q } : {}), ...(source ? { source } : {}), ...(createdFrom ? { created_from: createdFrom } : {}) } })).data;
  },
  async candidate(clinicId: number, id: number) {
    return (await api.get<LearningCandidate>(`${base(clinicId)}/candidates/${id}`)).data;
  },
  async library(clinicId: number, page = 1, pageSize = 20, q = '', source = '', createdFrom = '') {
    return (await api.get<LearningPage<LearningItem>>(`${base(clinicId)}/library`, { params: { page, page_size: pageSize, ...(q ? { q } : {}), ...(source ? { source } : {}), ...(createdFrom ? { created_from: createdFrom } : {}) } })).data;
  },
  async libraryItem(clinicId: number, id: number) {
    return (await api.get<LearningItem>(`${base(clinicId)}/library/${id}`)).data;
  },
  async approve(clinicId: number, id: number, fields: LearningEdit = {}) {
    return (await api.post<LearningItem>(`${base(clinicId)}/candidates/${id}/approve`, fields)).data;
  },
  async discard(clinicId: number, id: number) {
    return (await api.post(`${base(clinicId)}/candidates/${id}/discard`)).data;
  },
  async editItem(clinicId: number, id: number, version: number, fields: LearningEdit) {
    return (await api.patch<LearningItem>(`${base(clinicId)}/library/${id}`, { ...fields, expected_version: version })).data;
  },
  async setItemActive(clinicId: number, id: number, version: number, active: boolean) {
    return (await api.post<LearningItem>(`${base(clinicId)}/library/${id}/${active ? 'restore' : 'deactivate'}`, { expected_version: version })).data;
  },
  async previewHistory(clinicId: number, fromDate: string, toDate: string, maxMessages = 100) {
    return (await api.post<LearningHistoryPreview>(`${base(clinicId)}/history/preview`, { from_date: fromDate, to_date: toDate, max_messages: maxMessages })).data;
  },
  async startHistory(clinicId: number, fromDate: string, toDate: string, maxMessages = 100) {
    return (await api.post<LearningJob>(`${base(clinicId)}/history/start`, { from_date: fromDate, to_date: toDate, max_messages: maxMessages })).data;
  },
  async job(clinicId: number, id: number) {
    return (await api.get<LearningJob>(`${base(clinicId)}/jobs/${id}`)).data;
  },
  async retryJob(clinicId: number, id: number) {
    return (await api.post<LearningJob>(`${base(clinicId)}/jobs/${id}/retry`, {})).data;
  },
  async messageContext(clinicId: number, messageId: number) {
    return (await api.get<LearningContext>(`${base(clinicId)}/messages/${messageId}/learning-context`)).data;
  },
  async selectMessageContext(clinicId: number, messageId: number, selection: LearningContextSelection) {
    return (await api.post<LearningContext>(`${base(clinicId)}/messages/${messageId}/learning-context`, selection)).data;
  },
  async correctMessage(clinicId: number, messageId: number, response: string, idempotencyKey: string, instruction?: string, action: MessageLearningAction = 'correction', context?: LearningCorrectionContext) {
    return (await api.post<LearningItem>(`${base(clinicId)}/messages/${messageId}/corrections`, {
      action,
      response,
      ...(instruction?.trim() ? { instruction: instruction.trim() } : {}),
      idempotency_key: idempotencyKey,
      ...(context || {}),
    })).data;
  },
};

export const activeClinicId = (): number | null => {
  const id = Number(localStorage.getItem('clinic_id'));
  return Number.isInteger(id) && id > 0 ? id : null;
};
